import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, concatMap, withLatestFrom, mergeMap, catchError } from 'rxjs/operators';

import { RouterReducerState } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { createEffect, ofType, Actions } from '@ngrx/effects';

import { ReferenceService, RouterStateUrl } from '@sd-portal/shared/util';

import { PersonProfileAuditService } from '../../person-profile-audit.service';
import { State } from '..';
import * as actions from './person-profile.actions';

@Injectable()
export class PersonProfileEffects {
  constructor(
    private readonly personProfileAuditService: PersonProfileAuditService,
    private readonly referenceService: ReferenceService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadAircraftProfileAuditSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPersonProfileAuditSkeleton),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const personId = +state.router.state.params['id'];

        return this.personProfileAuditService.getPersonProfileSkeleton(personId).pipe(
          map(payload => actions.PersonProfileAuditSkeletonLoaded({ payload })),
          catchError(err => of(actions.PersonProfileAuditSkeletonLoadError(err)))
        );
      })
    )
  );

  // PersonProfile

  loadPersonProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPersonProfileAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPersonProfileAudits(action.personId).pipe(
          map(audits => actions.PersonProfileAuditLoaded({ personId: action.personId, audits })),
          catchError(err => of(actions.PersonProfileAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePersonProfileAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePersonProfileAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.personProfileAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPersonProfileAudits(action.personId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.PersonProfileAuditLoaded({ personId: action.personId, audits })),
            catchError(err => of(actions.PersonProfileAuditLoadError(err)))
          );
      })
    )
  );

  // BusinessPerson
  loadBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadBusinessPersonAudit),
      mergeMap(action =>
        this.personProfileAuditService.getBusinessPersonAudits(action.businessPersonId).pipe(
          map(audits =>
            actions.BusinessPersonAuditLoaded({
              businessPersonId: action.businessPersonId,
              audits
            })
          ),
          catchError(err => of(actions.BusinessPersonAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageBusinessPersonAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.businessPersonAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getBusinessPersonAudits(
            action.businessPersonId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.BusinessPersonAuditLoaded({
                businessPersonId: action.businessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.BusinessPersonAuditLoadError(err)))
          );
      })
    )
  );

  // PhoneNumber
  loadPhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPhoneNumberAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPhoneNumberAudits(action.phoneNumberId).pipe(
          map(audits =>
            actions.PhoneNumberAuditLoaded({ phoneNumberId: action.phoneNumberId, audits })
          ),
          catchError(err => of(actions.PhoneNumberAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePhoneNumberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.phoneNumberAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPhoneNumberAudits(action.phoneNumberId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PhoneNumberAuditLoaded({ phoneNumberId: action.phoneNumberId, audits })
            ),
            catchError(err => of(actions.PhoneNumberAuditLoadError(err)))
          );
      })
    )
  );

  // EmailAddress
  loadEmailAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEmailAddressAudit),
      mergeMap(action =>
        this.personProfileAuditService.getEmailAddressAudits(action.emailAddressId).pipe(
          map(audits =>
            actions.EmailAddressAuditLoaded({
              emailAddressId: action.emailAddressId,
              audits
            })
          ),
          catchError(err => of(actions.EmailAddressAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageEmailAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEmailAddressAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.emailAddressAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getEmailAddressAudits(action.emailAddressId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.EmailAddressAuditLoaded({
                emailAddressId: action.emailAddressId,
                audits
              })
            ),
            catchError(err => of(actions.EmailAddressAuditLoadError(err)))
          );
      })
    )
  );

  // Address
  loadAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadAddressAudit),
      mergeMap(action =>
        this.personProfileAuditService.getAddressAudits(action.addressId).pipe(
          map(audits => actions.AddressAuditLoaded({ addressId: action.addressId, audits })),
          catchError(err => of(actions.AddressAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageAddressAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageAddressAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.addressAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getAddressAudits(action.addressId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.AddressAuditLoaded({ addressId: action.addressId, audits })),
            catchError(err => of(actions.AddressAuditLoadError(err)))
          );
      })
    )
  );

  // EmergencyContact
  loadEmergencyContactAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEmergencyContactAudit),
      mergeMap(action =>
        this.personProfileAuditService.getEmergencyContactAudits(action.emergencyContactId).pipe(
          map(audits =>
            actions.EmergencyContactAuditLoaded({
              emergencyContactId: action.emergencyContactId,
              audits
            })
          ),
          catchError(err => of(actions.EmergencyContactAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageEmergencyContactAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEmergencyContactAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.emergencyContactAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getEmergencyContactAudits(
            action.emergencyContactId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.EmergencyContactAuditLoaded({
                emergencyContactId: action.emergencyContactId,
                audits
              })
            ),
            catchError(err => of(actions.EmergencyContactAuditLoadError(err)))
          );
      })
    )
  );

  // EmergencyContactPhoneNumber
  loadEmergencyContactPhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadEmergencyContactPhoneNumberAudit),
      mergeMap(action =>
        this.personProfileAuditService
          .getEmergencyContactPhoneNumberAudits(action.emergencyContactPhoneNumberId)
          .pipe(
            map(audits =>
              actions.EmergencyContactPhoneNumberAuditLoaded({
                emergencyContactPhoneNumberId: action.emergencyContactPhoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.EmergencyContactPhoneNumberAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageEmergencyContactPhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageEmergencyContactPhoneNumberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.personProfile.emergencyContactPhoneNumberAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getEmergencyContactPhoneNumberAudits(
            action.emergencyContactPhoneNumberId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.EmergencyContactPhoneNumberAuditLoaded({
                emergencyContactPhoneNumberId: action.emergencyContactPhoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.EmergencyContactPhoneNumberAuditLoadError(err)))
          );
      })
    )
  );

  // PersonAttribute
  loadPersonAttributeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPersonAttributeAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPersonAttributeAudits(action.personAttributeId).pipe(
          map(audits =>
            actions.PersonAttributeAuditLoaded({
              personAttributeId: action.personAttributeId,
              audits
            })
          ),
          catchError(err => of(actions.PersonAttributeAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePersonAttributeAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePersonAttributeAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.personAttributeAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPersonAttributeAudits(
            action.personAttributeId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.PersonAttributeAuditLoaded({
                personAttributeId: action.personAttributeId,
                audits
              })
            ),
            catchError(err => of(actions.PersonAttributeAuditLoadError(err)))
          );
      })
    )
  );

  // Staff Group
  loadPersonStaffGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPersonStaffGroupAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPersonStaffGroupAudits(action.personStaffGroupId).pipe(
          map(audits =>
            actions.PersonStaffGroupAuditLoaded({
              personStaffGroupId: action.personStaffGroupId,
              audits
            })
          ),
          catchError(err => of(actions.PersonStaffGroupAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePersonStaffGroupAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePersonStaffGroupAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.personStaffGroupAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPersonStaffGroupAudits(
            action.personStaffGroupId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.StaffInfoAuditLoaded({ staffInfoId: action.personStaffGroupId, audits })
            ),
            catchError(err => of(actions.StaffInfoAuditLoadError(err)))
          );
      })
    )
  );

  // StaffInfo
  loadStaffInfoAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadStaffInfoAudit),
      mergeMap(action =>
        this.personProfileAuditService.getStaffInfoAudits(action.staffInfoId).pipe(
          map(audits => actions.StaffInfoAuditLoaded({ staffInfoId: action.staffInfoId, audits })),
          catchError(err => of(actions.StaffInfoAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageStaffInfoAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageStaffInfoAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.staffInfoAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getStaffInfoAudits(action.staffInfoId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.StaffInfoAuditLoaded({ staffInfoId: action.staffInfoId, audits })
            ),
            catchError(err => of(actions.StaffInfoAuditLoadError(err)))
          );
      })
    )
  );

  loadCrewMemberTypeRatingTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewMemberTypeRatingTypes),
      mergeMap(() =>
        this.referenceService.getReferenceItems('AircraftTypeRating').pipe(
          map(payload => actions.CrewMemberTypeRatingTypesLoaded({ payload })),
          catchError(err => of(actions.CrewMemberTypeRatingTypesLoaded(err)))
        )
      )
    )
  );

  // Preferences
  loadPreferenceCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPreferenceCateringAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPreferenceAudits(action.preferenceId).pipe(
          map(audits =>
            actions.PreferenceCateringAuditLoaded({
              preferenceId: action.preferenceId,
              audits
            })
          ),
          catchError(err => of(actions.PreferenceCateringAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePreferenceCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePreferenceCateringAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.preferenceCateringAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPreferenceAudits(action.preferenceId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PreferenceCateringAuditLoaded({
                preferenceId: action.preferenceId,
                audits
              })
            ),
            catchError(err => of(actions.PreferenceCateringAuditLoadError(err)))
          );
      })
    )
  );

  loadPreferenceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPreferenceGroundTransportationAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPreferenceAudits(action.preferenceId).pipe(
          map(audits =>
            actions.PreferenceGroundTransportationAuditLoaded({
              preferenceId: action.preferenceId,
              audits
            })
          ),
          catchError(err => of(actions.PreferenceGroundTransportationAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePreferenceAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePreferenceGroundTransportationAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.personProfile.preferenceGroundTransportationAudit.lastAuditLogId;
        }

        return this.personProfileAuditService
          .getPreferenceAudits(action.preferenceId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PreferenceGroundTransportationAuditLoaded({
                preferenceId: action.preferenceId,
                audits
              })
            ),
            catchError(err => of(actions.PreferenceGroundTransportationAuditLoadError(err)))
          );
      })
    )
  );

  loadPreferenceHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPreferenceHotelAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPreferenceAudits(action.preferenceId).pipe(
          map(audits =>
            actions.PreferenceHotelAuditLoaded({
              preferenceId: action.preferenceId,
              audits
            })
          ),
          catchError(err => of(actions.PreferenceHotelAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePreferenceHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePreferenceHotelAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.preferenceHotelAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPreferenceAudits(action.preferenceId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PreferenceHotelAuditLoaded({
                preferenceId: action.preferenceId,
                audits
              })
            ),
            catchError(err => of(actions.PreferenceHotelAuditLoadError(err)))
          );
      })
    )
  );

  // ExecutiveAssociate
  loadExecutiveAssociateAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExecutiveAssociateAudit),
      mergeMap(action =>
        this.personProfileAuditService
          .getExecutiveAssociateAudits(action.executiveAssociateId)
          .pipe(
            map(audits =>
              actions.ExecutiveAssociateAuditLoaded({
                executiveAssociateId: action.executiveAssociateId,
                audits
              })
            ),
            catchError(err => of(actions.ExecutiveAssociateAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageExecutiveAssociateAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExecutiveAssociateAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.executiveAssociateAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getExecutiveAssociateAudits(
            action.executiveAssociateId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ExecutiveAssociateAuditLoaded({
                executiveAssociateId: action.executiveAssociateId,
                audits
              })
            ),
            catchError(err => of(actions.ExecutiveAssociateAuditLoadError(err)))
          );
      })
    )
  );

  loadExecutiveAssociatePhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExecutiveAssociatePhoneNumberAudit),
      mergeMap(action =>
        this.personProfileAuditService
          .getPhoneNumberAudits(action.executiveAssociatePhoneNumberId)
          .pipe(
            map(audits =>
              actions.ExecutiveAssociatePhoneNumberAuditLoaded({
                executiveAssociatePhoneNumberId: action.executiveAssociatePhoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.ExecutiveAssociatePhoneNumberAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageExecutiveAssociatePhoneNumberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExecutiveAssociatePhoneNumberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.executiveAssociateAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPhoneNumberAudits(
            action.executiveAssociatePhoneNumberId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ExecutiveAssociatePhoneNumberAuditLoaded({
                executiveAssociatePhoneNumberId: action.executiveAssociatePhoneNumberId,
                audits
              })
            ),
            catchError(err => of(actions.ExecutiveAssociatePhoneNumberAuditLoadError(err)))
          );
      })
    )
  );

  loadExecutiveAssociateEmailAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadExecutiveAssociateEmailAudit),
      mergeMap(action =>
        this.personProfileAuditService.getEmailAddressAudits(action.executiveAssociateEmailId).pipe(
          map(audits =>
            actions.ExecutiveAssociateEmailAuditLoaded({
              executiveAssociateEmailId: action.executiveAssociateEmailId,
              audits
            })
          ),
          catchError(err => of(actions.ExecutiveAssociateEmailAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageExecutiveAssociateEmailAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageExecutiveAssociateEmailAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.executiveAssociateAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getEmailAddressAudits(
            action.executiveAssociateEmailId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ExecutiveAssociateEmailAuditLoaded({
                executiveAssociateEmailId: action.executiveAssociateEmailId,
                audits
              })
            ),
            catchError(err => of(actions.ExecutiveAssociateEmailAuditLoadError(err)))
          );
      })
    )
  );

  // CrewMemberTypeRating
  loadCrewMemberTypeRatingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewMemberTypeRatingAudit),
      mergeMap(action =>
        this.personProfileAuditService
          .getCrewMemberTypeRatingAudits(action.crewMemberTypeRatingId)
          .pipe(
            map(audits =>
              actions.CrewMemberTypeRatingAuditLoaded({
                crewMemberTypeRatingId: action.crewMemberTypeRatingId,
                audits
              })
            ),
            catchError(err => of(actions.CrewMemberTypeRatingAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageCrewMemberTypeRatingAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewMemberTypeRatingAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.crewMemberTypeRatingAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getCrewMemberTypeRatingAudits(
            action.crewMemberTypeRatingId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.CrewMemberTypeRatingAuditLoaded({
                crewMemberTypeRatingId: action.crewMemberTypeRatingId,
                audits
              })
            ),
            catchError(err => of(actions.CrewMemberTypeRatingAuditLoadError(err)))
          );
      })
    )
  );

  // TaxInfo

  loadTaxInfoAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTaxInfoAudit),
      mergeMap(action =>
        this.personProfileAuditService.getTaxInfoAudits(action.taxInfoId).pipe(
          map(audits => actions.TaxInfoAuditLoaded({ taxInfoId: action.taxInfoId, audits })),
          catchError(err => of(actions.TaxInfoAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageTaxInfoAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTaxInfoAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.taxInfoAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getTaxInfoAudits(action.taxInfoId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.TaxInfoAuditLoaded({ taxInfoId: action.taxInfoId, audits })),
            catchError(err => of(actions.TaxInfoAuditLoadError(err)))
          );
      })
    )
  );

  // Passenger
  loadPassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPassengerAudit),
      mergeMap(action =>
        this.personProfileAuditService.getPassengerAudits(action.passengerId).pipe(
          map(audits => actions.PassengerAuditLoaded({ passengerId: action.passengerId, audits })),
          catchError(err => of(actions.PassengerAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPagePassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePassengerAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.personProfile.taxInfoAudit.lastAuditLogId;
        }
        return this.personProfileAuditService
          .getPassengerAudits(action.passengerId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.PassengerAuditLoaded({ passengerId: action.passengerId, audits })
            ),
            catchError(err => of(actions.PassengerAuditLoadError(err)))
          );
      })
    )
  );
}
